import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/AccountApplicationsService';

import {AccountApplication} from '../types';

// FNP-946: records are React Query's job; selection stays in Redux (FNP-951).
//
// The list is scoped by accountId, so accountId is part of the query key -
// two accounts must not share a cache entry. The dashboard consumes this
// through useAccountApplications with the signed-in user's account id.

export const accountApplicationsKeys = {
    all: ['account-applications'] as const,
    lists: () => [...accountApplicationsKeys.all, 'list'] as const,
    list: (accountId?: string) => [...accountApplicationsKeys.lists(), accountId ?? ''] as const,
    details: () => [...accountApplicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...accountApplicationsKeys.details(), id] as const,
};

export const useAccountApplicationsQuery = (accountId?: string) => {
    return useQuery({
        queryKey: accountApplicationsKeys.list(accountId),
        queryFn: async (): Promise<AccountApplication[]> => {
            // listAccountApplications returns {items}, not a bare array.
            const connection = await fetchRecords({accountId: accountId ?? ''});
            return connection?.items ?? [];
        },
        // Without this the query fires with an empty accountId before the user
        // account has resolved, which returns everyone's applications or none.
        enabled: !!accountId,
    });
};

export const useAccountApplicationQuery = (accountApplicationId?: string) => {
    return useQuery({
        queryKey: accountApplicationsKeys.detail(accountApplicationId ?? ''),
        queryFn: () => fetchOne(accountApplicationId as string),
        enabled: !!accountApplicationId,
    });
};

export const useCreateAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: AccountApplication) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: accountApplicationsKeys.lists()});
        },
    });
};

export const useUpdateAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: AccountApplication }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: accountApplicationsKeys.lists()});
            queryClient.invalidateQueries({queryKey: accountApplicationsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: accountApplicationsKeys.lists()});
        },
    });
};
